/**
 * ContextLinkBanner Component
 *
 * Shows when a checklist is being linked to a voyage, maintenance job, etc.
 * Allows user to confirm, change, or unlink the context.
 */

import React from 'react';
import { Anchor, Ship, Wrench, AlertCircle, Link2, X, ChevronDown } from 'lucide-react';

const CONTEXT_ICONS = {
  voyage: Ship,
  maintenance_job: Wrench,
  equipment: AlertCircle,
  defect: AlertCircle
};

const CONTEXT_LABELS = {
  voyage: 'Active Voyage',
  maintenance_job: 'Maintenance Job',
  equipment: 'Equipment',
  defect: 'Defect'
};

const ContextLinkBanner = ({
  linkedContext,
  onUnlink,
  onChangeContext,
  isAutoLinked = true
}) => {
  if (!linkedContext) return null;

  const IconComponent = CONTEXT_ICONS[linkedContext.type] || Link2;
  const contextLabel = CONTEXT_LABELS[linkedContext.type] || 'Context';

  return (
    <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-3 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-cyan-100 rounded-lg">
            <Anchor className="h-4 w-4 text-cyan-600" />
          </div>
          <div>
            <p className="text-xs text-cyan-600 font-medium">
              Linking to {contextLabel}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              <IconComponent className="h-4 w-4 text-cyan-700" />
              <span className="text-sm font-semibold text-cyan-800">
                {linkedContext.id}
              </span>
              {linkedContext.name && (
                <span className="text-sm text-cyan-600">
                  • {linkedContext.name}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isAutoLinked && (
            <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 text-xs rounded-full">
              Auto
            </span>
          )}
          {onChangeContext && (
            <button
              onClick={onChangeContext}
              className="p-1.5 text-cyan-600 hover:bg-cyan-100 rounded-lg transition-colors"
              title="Change context"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
          )}
          {onUnlink && (
            <button
              onClick={onUnlink}
              className="p-1.5 text-cyan-600 hover:bg-cyan-100 rounded-lg transition-colors"
              title="Unlink"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContextLinkBanner;
